/* eslint-disable max-lines */
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { CameraSource } from "@capacitor/camera";
import {
  BottomSheet,
  Input,
  LoadingSpinner,
  Text,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { isDefined, toError } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { FullScreenDialog, isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useBrowserOnFileChange } from "@src/appV2/lib/Media/useBrowserOnFileChange";
import { useMobileOnFileSelect } from "@src/appV2/lib/Media/useMobileOnFileSelect";
import { useTakePhoto } from "@src/appV2/lib/Media/useTakePhoto";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { FileUploaderBottomSheet } from "@src/appV2/redesign/components/FileUploaderBottomSheet";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { DocumentSubmissionInProgressBottomSheet } from "@src/appV2/redesign/Documents/Submission/InProgressBottomSheet";
import { useNewSmoochChat } from "@src/appV2/support/hooks/useSupportChat";
import { useUploadSmoochAttachment } from "@src/appV2/support/hooks/useUploadSmoochAttachment";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback, useRef, useState } from "react";
import Smooch from "smooch";

import { type PlacementCandidate } from "../../../PlacementCandidate/types";
import { type PlacementDetail } from "../../api/useGetPlacement";
import { SupportChatMessageList } from "./MessageList";
import { useSupportChatEmbeddedHelloSign } from "./useCompositeDocumentHelloSign";
import { usePollingWorkerRequirements } from "./usePollingWorkerRequirements";
import { useScrollToBottomOnNewMessage } from "./useScrollToBottomOnNewMessage";

interface ChildcareSupportChatBottomSheetProps {
  modalState: UseModalState;
  placementDetails: PlacementDetail;
  placementCandidate: PlacementCandidate;
  isLoading: boolean;
  onClose: () => void;
}

export function ChildcareSupportChatBottomSheet(props: ChildcareSupportChatBottomSheetProps) {
  const { modalState, placementDetails, placementCandidate, isLoading, onClose } = props;
  const { userId: workerId } = useDefinedWorker();
  const deviceTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { conversationId, isReady, messages } = useNewSmoochChat({ workerId });
  const [messageInput, setMessageInput] = useState("");
  const fileInputId = "childcare-support-chat-file-input";
  const { showErrorToast, showSuccessToast } = useToast();
  const inputFileRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const { mutateAsync: uploadAttachment } = useUploadSmoochAttachment();
  const fileUploaderModalState = useModalState();

  const { hasMissingOrPendingDocuments } = usePollingWorkerRequirements({
    placementId: placementDetails.id,
    workerId,
    enabled: modalState.modalIsOpen,
  });

  const [hasSignedDocument, setHasSignedDocument] = useState(false);

  const {
    isHelloSignDocument,
    helloSignButtonText,
    isLoadingHelloSign,
    handleSignDocument,
    helloSignIframeContainerRef,
    helloSignFormModalState,
    submittingDocumentModalState,
  } = useSupportChatEmbeddedHelloSign({
    placementDetails,
    onSignSuccess: () => {
      setHasSignedDocument(true);
    },
  });

  const shouldShowHelloSignButton =
    isHelloSignDocument && !hasSignedDocument && isReady && messages.length > 0;

  const canContinue = isReady && !hasMissingOrPendingDocuments;

  useScrollToBottomOnNewMessage({
    messagesCount: messages.length,
    scrollContainerRef: messagesContainerRef,
  });

  const handleUploadSuccess = async (file: UploadFileData) => {
    try {
      if (!conversationId) {
        throw new Error("No conversation ID available");
      }

      fileUploaderModalState.closeModal();
      await uploadAttachment({
        file,
        conversationId,
      });

      showSuccessToast("File uploaded successfully");
    } catch (_error: unknown) {
      const error = toError(_error);
      showErrorToast(`Error uploading file: ${error.message}`);
    }
  };

  const handleUploadError = (error: Error) => {
    showErrorToast(`Error selecting file: ${error.message}`);
  };

  const handleTakePhoto = useTakePhoto({
    source: CameraSource.Camera,
    onSuccess: handleUploadSuccess,
    onError: handleUploadError,
  });

  const handleSelectPhotoFromCameraRoll = useTakePhoto({
    source: CameraSource.Photos,
    onSuccess: handleUploadSuccess,
    onError: handleUploadError,
  });

  const handleSelectFile = useMobileOnFileSelect({
    onSuccess: handleUploadSuccess,
    onError: handleUploadError,
  });

  const browserSelectFile = useCallback(async () => {
    inputFileRef?.current?.click();
  }, []);

  const browserOnFileChange = useBrowserOnFileChange({
    onSuccess: handleUploadSuccess,
    onError: handleUploadError,
  });

  const onTakePhoto = isCapacitorPlatform() ? handleTakePhoto : handleSelectPhotoFromCameraRoll;
  const onSelectPhotoFromCameraRoll = handleSelectPhotoFromCameraRoll;
  const onSelectFile = isCapacitorPlatform() ? handleSelectFile : browserSelectFile;

  const eventContext = {
    workerId,
    placementId: placementDetails.id,
    placementCandidateId: placementCandidate.id,
  };

  useLogEffect(APP_V2_APP_EVENTS.CHILDCARE_SUPPORT_CHAT_MODAL_VIEWED, eventContext);

  const handleClose = () => {
    modalState.closeModal();
    onClose();
  };

  const handleDismiss = () => {
    logEvent(APP_V2_APP_EVENTS.CHILDCARE_SUPPORT_CHAT_EXITED, eventContext);
    modalState.closeModal();
  };

  const canSend = isReady && messageInput.trim().length > 0 && conversationId;
  const handleSendMessage = async () => {
    if (!canSend) {
      return;
    }

    Smooch.sendMessage(messageInput.trim(), conversationId);

    setMessageInput("");
  };

  if (isLoading) {
    return (
      <BottomSheet modalState={modalState} header={<BottomSheetHeader onClose={handleDismiss} />}>
        <Text>Loading...</Text>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{ height: "90vh" }}
      header={
        <BottomSheetHeader onClose={handleDismiss}>
          <Stack direction="row" alignItems="center">
            <Box sx={(theme) => ({ width: theme.spacing(9) })} />
            <Text sx={{ flex: 1, textAlign: "center" }} variant="h6">
              Pending Documents
            </Text>
          </Stack>
        </BottomSheetHeader>
      }
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "100vh",
          overflow: "hidden",
        }}
      >
        <Box
          ref={messagesContainerRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 5,
            py: 6,
            minHeight: 0,
          }}
        >
          {!conversationId && messages.length === 0 ? (
            <Stack
              spacing={4}
              sx={{
                height: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <LoadingSpinner size="medium" variant="primary" />
              <Text variant="body1" color="text.secondary">
                Connecting support chat...
              </Text>
            </Stack>
          ) : (
            <SupportChatMessageList messages={messages} timezone={deviceTimezone} />
          )}
        </Box>
        <Box sx={{ px: 5, pb: 6 }}>
          <Stack spacing={4}>
            {shouldShowHelloSignButton && (
              <Button
                fullWidth
                variant="outlined"
                size="large"
                disabled={isLoadingHelloSign}
                onClick={() => {
                  void handleSignDocument?.();
                }}
              >
                {helloSignButtonText ?? "Sign Document"}
              </Button>
            )}
            {canContinue && (
              <Button fullWidth variant="contained" size="large" onClick={handleClose}>
                Continue
              </Button>
            )}
            <Stack direction="row" spacing={3} alignItems="center">
              <IconButton
                variant="outlined"
                aria-label="attach-file"
                disabled={!isReady || !isDefined(conversationId)}
                size="large"
                iconType="paperclip"
                onClick={fileUploaderModalState.openModal}
              />
              <Input
                placeholder="Enter message"
                value={messageInput}
                aria-label="chat-text-input"
                disabled={!isReady || !isDefined(conversationId)}
                sx={{ flex: 1 }}
                onEnterPress={() => {
                  if (canSend) {
                    void handleSendMessage();
                  }
                }}
                onChange={(event) => {
                  setMessageInput(event.target.value);
                }}
              />
            </Stack>
          </Stack>
        </Box>
      </Box>
      <input
        ref={inputFileRef}
        id={fileInputId}
        type="file"
        style={{ display: "none" }}
        accept="application/pdf,image/png,image/jpeg,image/jpg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={browserOnFileChange}
      />
      <FileUploaderBottomSheet
        modalState={fileUploaderModalState}
        onTakePhoto={onTakePhoto}
        onSelectPhotoFromCameraRoll={onSelectPhotoFromCameraRoll}
        onSelectFile={onSelectFile}
      />
      {isDefined(helloSignFormModalState) && (
        <FullScreenDialog modalState={helloSignFormModalState}>
          <Box ref={helloSignIframeContainerRef} sx={{ height: "100%" }} />
        </FullScreenDialog>
      )}
      {isDefined(submittingDocumentModalState) && (
        <DocumentSubmissionInProgressBottomSheet modalState={submittingDocumentModalState} />
      )}
    </BottomSheet>
  );
}
/* eslint-enable max-lines */
